import React, { useCallback, useEffect, useState } from 'react';
import './EditProjectModal.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface EditableProject {
  id: string;
  name: string;
  description: string | null;
  default_linked_account_id?: string | null;
}

export type EditProjectTab = 'general' | 'share';

interface ShareRow {
  id: string;
  email: string;
  status: 'pending' | 'accepted' | 'revoked';
  expiresAt: string | null;
}

interface InviteDraft {
  email: string;
  date: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_DEMO_DAYS = 30;

const toDateInput = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const defaultExpiry = () => toDateInput(new Date(Date.now() + DEFAULT_DEMO_DAYS * 86400_000));
// End of the chosen day in the viewer's timezone.
const dateToIso = (date: string) => new Date(`${date}T23:59:59`).toISOString();

interface Props {
  project: EditableProject;
  initialTab?: EditProjectTab;
  onClose: () => void;
  onSaved: (project: any) => void;
}

interface AccountOption {
  id: string;
  label: string;
  tenant: boolean;
}

const EditProjectModal: React.FC<Props> = ({ project, initialTab = 'general', onClose, onSaved }) => {
  const [tab, setTab] = useState<EditProjectTab>(initialTab);
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description || '');
  const [defaultAccountId, setDefaultAccountId] = useState(project.default_linked_account_id || '');
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/accounts`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : { accounts: [] }))
      .then(data => {
        if (cancelled) return;
        setAccounts((data.accounts || [])
          .filter((a: any) => a.provider === 'youtube' && (a.status === 'connected' || a.status === 'syncing'))
          .map((a: any) => ({
            id: a.id,
            label: [a.profileData?.displayName, a.providerEmail].filter(Boolean).join(' · ') || 'YouTube account',
            tenant: a.linkRole === 'tenant',
          })));
      })
      .catch(() => { /* dropdown just stays empty */ });
    return () => { cancelled = true; };
  }, []);

  // ── Share as Demo tab ──
  const [shares, setShares] = useState<ShareRow[]>([]);
  const [sharesLoading, setSharesLoading] = useState(false);
  const [drafts, setDrafts] = useState<InviteDraft[]>([{ email: '', date: defaultExpiry() }]);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareError, setShareError] = useState('');
  const [shareNotice, setShareNotice] = useState('');
  // Invite links for emails that could not be sent, so the owner can pass them on by hand.
  const [manualLinks, setManualLinks] = useState<{ email: string; url: string; reason: string }[]>([]);

  const loadShares = useCallback(async () => {
    setSharesLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/projects/${project.id}/shares`, { credentials: 'include' });
      if (res.ok) setShares(await res.json());
    } catch { /* list stays empty */ }
    setSharesLoading(false);
  }, [project.id]);

  useEffect(() => { if (tab === 'share') loadShares(); }, [tab, loadShares]);

  const updateDraft = (i: number, patch: Partial<InviteDraft>) =>
    setDrafts(prev => prev.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));

  const sendInvites = async () => {
    setShareError('');
    setShareNotice('');
    const filled = drafts.filter(d => d.email.trim());
    if (filled.length === 0) { setShareError('Enter at least one email'); return; }
    const bad = filled.find(d => !EMAIL_RE.test(d.email.trim()) || !d.date);
    if (bad) { setShareError(`Check the email and expiry date for ${bad.email.trim() || 'a row'}`); return; }
    setShareBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/projects/${project.id}/shares`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          shareType: 'demo',
          invites: filled.map(d => ({ email: d.email.trim(), expiresAt: dateToIso(d.date) })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to send invites');
      const results: { email: string; ok: boolean; error?: string; emailSent?: boolean; emailError?: string; inviteUrl?: string }[] = data.results || [];
      const failed = results.filter(r => !r.ok);
      const sent = results.filter(r => r.ok);
      if (failed.length) setShareError(failed.map(f => `${f.email || 'row'}: ${f.error}`).join(' · '));
      if (sent.length) {
        const unsent = sent.filter(r => !r.emailSent);
        setShareNotice(`Invited ${sent.length} ${sent.length === 1 ? 'person' : 'people'}${unsent.length ? ` — ${unsent.length} email${unsent.length === 1 ? '' : 's'} not sent` : ''}.`);
        setManualLinks(unsent.filter(r => r.inviteUrl).map(r => ({ email: r.email, url: r.inviteUrl!, reason: r.emailError || '' })));
      }
      // Keep only the rows that failed so they can be corrected.
      const failedEmails = new Set(failed.map(f => f.email.toLowerCase()));
      const remaining = drafts.filter(d => failedEmails.has(d.email.trim().toLowerCase()));
      setDrafts(remaining.length ? remaining : [{ email: '', date: defaultExpiry() }]);
      await loadShares();
    } catch (err: any) {
      setShareError(err.message || 'Failed to send invites');
    } finally {
      setShareBusy(false);
    }
  };

  const changeExpiry = async (share: ShareRow, date: string) => {
    if (!date) return;
    setShareError('');
    try {
      const res = await fetch(`${API_BASE}/api/projects/${project.id}/shares/${share.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ expiresAt: dateToIso(date) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update expiry');
      await loadShares();
    } catch (err: any) {
      setShareError(err.message || 'Failed to update expiry');
    }
  };

  const revoke = async (share: ShareRow) => {
    if (!window.confirm(`Revoke access for ${share.email}?`)) return;
    setShareError('');
    try {
      const res = await fetch(`${API_BASE}/api/projects/${project.id}/shares/${share.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to revoke');
      setShares(prev => prev.filter(s => s.id !== share.id));
    } catch (err: any) {
      setShareError(err.message || 'Failed to revoke');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/projects/${project.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim(),
          defaultLinkedAccountId: defaultAccountId || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Failed' }));
        throw new Error(data.error || 'Failed to save project');
      }
      onSaved(await res.json());
    } catch (err: any) {
      setError(err.message || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Project</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="ep-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === 'general'} className={`ep-tab ${tab === 'general' ? 'active' : ''}`} onClick={() => setTab('general')}>General</button>
          <button type="button" role="tab" aria-selected={tab === 'share'} className={`ep-tab ${tab === 'share' ? 'active' : ''}`} onClick={() => setTab('share')}>Share as Demo</button>
        </div>

        {tab === 'share' ? (
          <div className="ep-share">
            <p className="cm-field-help">
              Invitees get read-only access: no editors and no downloads. Access ends on the expiry date.
            </p>
            {shareError && <div className="modal-error">{shareError}</div>}
            {shareNotice && <div className="ep-notice">{shareNotice}</div>}
            {manualLinks.map(l => (
              <div className="ep-manual-link" key={l.email}>
                <span>Send this link to {l.email}{l.reason ? ` (${l.reason})` : ''}:</span>
                <input readOnly value={l.url} aria-label={`Invite link for ${l.email}`} onFocus={e => e.currentTarget.select()} />
                <button type="button" className="btn-secondary" onClick={() => navigator.clipboard?.writeText(l.url)}>Copy</button>
              </div>
            ))}

            <h3 className="ep-subtitle">Invite</h3>
            {drafts.map((d, i) => (
              <div className="ep-invite-row" key={i}>
                <input
                  type="email"
                  placeholder="name@example.com"
                  aria-label="Invite email"
                  value={d.email}
                  onChange={e => updateDraft(i, { email: e.target.value })}
                />
                <input
                  type="date"
                  aria-label="Expiry date"
                  value={d.date}
                  min={toDateInput(new Date(Date.now() + 86400_000))}
                  onChange={e => updateDraft(i, { date: e.target.value })}
                />
                {drafts.length > 1 && (
                  <button type="button" className="ep-icon-btn" aria-label="Remove row" onClick={() => setDrafts(prev => prev.filter((_, idx) => idx !== i))}>✕</button>
                )}
              </div>
            ))}
            <div className="ep-invite-actions">
              <button type="button" className="btn-secondary" onClick={() => setDrafts(prev => [...prev, { email: '', date: defaultExpiry() }])}>+ Add another</button>
              <button type="button" className="btn-primary" disabled={shareBusy} onClick={sendInvites}>
                {shareBusy ? 'Sending...' : 'Send invites'}
              </button>
            </div>

            <h3 className="ep-subtitle">Shared with</h3>
            {sharesLoading ? (
              <p className="cm-field-help">Loading…</p>
            ) : shares.length === 0 ? (
              <p className="cm-field-help">Not shared with anyone yet.</p>
            ) : (
              <ul className="ep-share-list">
                {shares.map(sh => {
                  const expired = !!sh.expiresAt && new Date(sh.expiresAt).getTime() <= Date.now();
                  const label = expired ? 'Expired' : sh.status === 'accepted' ? 'Accepted' : 'Pending';
                  return (
                    <li className="ep-share-item" key={sh.id}>
                      <div className="ep-share-main">
                        <span className="ep-share-email">{sh.email}</span>
                        <span className={`ep-badge ep-badge-${label.toLowerCase()}`}>{label}</span>
                      </div>
                      <div className="ep-share-controls">
                        <input
                          type="date"
                          aria-label={`Expiry for ${sh.email}`}
                          value={sh.expiresAt ? toDateInput(new Date(sh.expiresAt)) : ''}
                          min={toDateInput(new Date(Date.now() + 86400_000))}
                          onChange={e => changeExpiry(sh, e.target.value)}
                        />
                        <button type="button" className="btn-secondary ep-revoke" onClick={() => revoke(sh)}>Revoke</button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={onClose}>Close</button>
            </div>
          </div>
        ) : (
        <form onSubmit={handleSubmit}>
          {error && <div className="modal-error">{error}</div>}

          <div className="modal-field">
            <label>Project Name *</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} autoFocus />
          </div>

          <div className="modal-field">
            <label>Description (optional)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} />
          </div>

          <div className="modal-field">
            <label>Default publishing account (YouTube)</label>
            <select value={defaultAccountId} onChange={e => setDefaultAccountId(e.target.value)}>
              <option value="">No default — choose when publishing</option>
              {accounts.map(a => (
                <option key={a.id} value={a.id}>{a.label}{a.tenant ? ' (tenant)' : ''}</option>
              ))}
            </select>
            <p className="cm-field-help">Pre-selected when composing a post. You can still change it there.</p>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default EditProjectModal;
